import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { REGEX_EMAIL, REGEX_NUMBERS } from '@/constants/regex'

import { ApplicationState } from '@/state'
import useDialog from '@/hooks/ui/use-dialog.hook'
import { DialogsEnum } from '@/enums'

import { usePlayerCardActions } from './'

export const usePlayerCardForm = () => {
  const { t } = useTranslation()

  const { updatePlayerCardInfo, getCountriesList } = usePlayerCardActions()

  const { handleCloseDialog, dialog } = useDialog()

  const { playerCard, countriesList } = useSelector(
    ({ playerCard }: ApplicationState) => playerCard,
  )

  const initialValuesPlayer = {
    email: playerCard?.email,
    mobileCountryCode: playerCard?.mobileCountryCode,
    mobileNumber: playerCard?.mobileNumber,
    dateOfBirth: playerCard?.dateOfBirth,
    language: playerCard?.language,
    gender: playerCard?.gender,
    country: playerCard?.country,
    state: playerCard?.state,
    city: playerCard?.city,
    zipCode: playerCard?.zipCode,
    address: playerCard?.address,
    currency: playerCard?.currency,
    emailOptIn: playerCard?.emailOptIn,
    smsOptIn: playerCard?.smsOptIn,
    registrationDate: playerCard?.registrationDate,
    registrationIp: playerCard?.registrationIp,
  }

  const requiredString = Yup.string().required(t('error.requiredField'))

  const validationSchemaPlayer = Yup.object().shape({
    email: requiredString.matches(
      REGEX_EMAIL,
      t('error.invalidField', { field: t('labels.email') }),
    ),
    dateOfBirth: requiredString,
    state: requiredString,
    city: requiredString,
    zipCode: requiredString,
    address: requiredString,
    mobileCountryCode: requiredString,
    mobileNumber: requiredString.matches(
      REGEX_NUMBERS,
      t('error.invalidField', { field: t('labels.mobileNumber') }),
    ),
  })

  const formikPlayer = useFormik({
    initialValues: initialValuesPlayer,
    validationSchema: validationSchemaPlayer,
    onSubmit: (values) => {
      const selectedCountry = countriesList.find(
        (country) => country.iso2 === values.mobileCountryCode,
      )

      if (!selectedCountry) return

      updatePlayerCardInfo({
        ...playerCard,
        email: values.email,
        mobileCountryCode: selectedCountry.phone[0].toString(),
        mobileNumber: values.mobileNumber,
        dateOfBirth: values.dateOfBirth,
        language: values.language,
        gender: values.gender,
        country: values.country,
        state: values.state,
        city: values.city,
        zipCode: values.zipCode,
        address: values.address,
        currency: values.currency,
        emailOptIn: values.emailOptIn,
        smsOptIn: values.smsOptIn,
      })
    },
    enableReinitialize: true,
  })

  useEffect(() => {
    formikPlayer.resetForm()
    if (
      dialog.open === true &&
      dialog.id === DialogsEnum.DIALOG_EDIT_PLAYER_DATA &&
      countriesList.length === 0
    ) {
      getCountriesList()
    }
  }, [dialog]) // eslint-disable-line

  useEffect(() => {
    if (dialog.open && countriesList.length > 0) {
      const iso2EqCountry = countriesList.find(
        (country) =>
          country.phone[0].toString() ===
            formikPlayer.values.mobileCountryCode &&
          formikPlayer.values.country === country.iso2,
      )?.iso2
      if (iso2EqCountry) {
        formikPlayer.setFieldValue('mobileCountryCode', iso2EqCountry)
      } else {
        const iso2EqPhone = countriesList.find(
          (country) =>
            country.phone[0].toString() ===
            formikPlayer.values.mobileCountryCode,
        )?.iso2
        formikPlayer.setFieldValue('mobileCountryCode', iso2EqPhone || '')
      }
    }
  }, [countriesList, dialog]) // eslint-disable-line

  return {
    dialog,
    formikPlayer,
    handleSubmit: formikPlayer.handleSubmit,
    handleCloseDialog,
  }
}
